import React, { useEffect, useCallback } from 'react';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { useDispatch } from 'react-redux';
import { useData, useStatus, isLoading } from 'resift';
import makeGetValuesOfField from 'fetches/rolesAndPermissions/makeGetValuesOfField';
import * as Types from 'pages/RolesAndPermissions/typings';

import CircularProgress from '@material-ui/core/CircularProgress';
import FreeformAndSearchInput from './FreeformAndSearchInput';

interface Props {
  input: string;
  fieldObjectKey: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelect: (newValue: string) => void;
}

function Container({ input, fieldObjectKey, onChange, onSelect }: Props) {
  const dispatch = useDispatch();
  const getValuesOfField = makeGetValuesOfField(fieldObjectKey || '');
  const valuesOfFieldResponse = useData(getValuesOfField);
  const getValuesOfFieldStatus = useStatus(getValuesOfField);

  const debouncedDispatch = useCallback(
    _debounce((searchValue: string) => {
      dispatch(getValuesOfField(searchValue));
    }, 500),
    [dispatch, getValuesOfField],
  );

  useEffect(() => {
    dispatch(getValuesOfField());
  }, [dispatch, getValuesOfField, fieldObjectKey]);

  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  if (isLoading(getValuesOfFieldStatus)) {
    return <CircularProgress color="secondary" />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedDispatch(e.target.value);
    onChange(e);
  };

  const options = _get(valuesOfFieldResponse, 'results', []) as Array<Types.FieldValue>;

  return (
    <FreeformAndSearchInput
      input={input}
      options={options}
      onChange={handleChange}
      onSelect={onSelect}
    />
  );
}

export default Container;
